<script setup lang="ts">
  import { computed, ref, watch, onBeforeUnmount } from "vue";
  import { useEditor } from "@tiptap/vue-3";
  import StarterKit from "@tiptap/starter-kit";
  import Placeholder from "@tiptap/extension-placeholder";
  import { TiptapProvider, TiptapToolbar, TiptapContent } from "@/components/episto-ui/tiptap";
  import { createVariableMentionExtension, type VariableItem } from "./variable-mention";

  const props = withDefaults(
    defineProps<{
      modelValue?: string;
      placeholder?: string;
      disabled?: boolean;
      readonly?: boolean;
      mentionItems?: VariableItem[] | null;
    }>(),
    {
      modelValue: "",
      placeholder: "Enter text...",
      disabled: false,
      readonly: false,
      mentionItems: null,
    },
  );

  const emit = defineEmits<{ "update:modelValue": [value: string] }>();

  const isFocused = ref(false);
  const isEditable = computed(() => !props.disabled && !props.readonly);

  const editor = useEditor({
    content: props.modelValue,
    editable: isEditable.value,
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: props.placeholder,
      }),
      ...(props.mentionItems ? [createVariableMentionExtension(props.mentionItems)] : []),
    ],
    onUpdate: ({ editor }) => {
      emit("update:modelValue", editor.getHTML());
    },
    onFocus: () => {
      isFocused.value = true;
    },
    onBlur: () => {
      setTimeout(() => {
        isFocused.value = false;
      }, 150);
    },
  });

  watch(
    () => props.modelValue,
    (newValue) => {
      if (editor.value && newValue !== editor.value.getHTML()) {
        editor.value.commands.setContent(newValue, false);
      }
    },
  );

  watch(isEditable, (editable) => editor.value?.setEditable(editable));

  onBeforeUnmount(() => {
    editor.value?.destroy();
  });
</script>

<template>
  <div
    class="border rounded-md transition-all cursor-text"
    :class="{
      'ring-2 ring-primary/20 border-primary': isFocused,
      'opacity-50 pointer-events-none': disabled,
    }"
    @click="editor?.commands.focus()">
    <TiptapProvider
      v-if="editor"
      :editor="editor">
      <div class="flex items-center border-b bg-muted/30">
        <TiptapToolbar
          variant="mini"
          class="flex-1 px-1 py-0.5 gap-0.5" />
        <div
          v-if="$slots['toolbar-end']"
          class="shrink-0 px-1">
          <slot name="toolbar-end" />
        </div>
      </div>
      <TiptapContent class="min-h-[3rem] bg-white rounded-b-md [&_.ProseMirror]:min-h-[2rem] [&_.variable-mention]:bg-primary/15 [&_.variable-mention]:text-primary [&_.variable-mention]:border [&_.variable-mention]:border-primary/30 [&_.variable-mention]:rounded [&_.variable-mention]:px-1 [&_.variable-mention]:text-[0.85em] [&_.variable-mention]:font-medium" />
    </TiptapProvider>
  </div>
</template>
